import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'

import { useOktaUserQuery } from '../../graphql/graphql'
import { useAccessLevel } from '../../context/AccessLevelContext'
import { AppFunction } from '../../auth/roles'

import 'primereact/resources/themes/lara-light-blue/theme.css'
import 'primereact/resources/primereact.min.css'
import 'primeicons/primeicons.css'

interface PIITableProps {
  email: string
}

export function OktaPIIComponent({ email }: PIITableProps) {
  const { isAuthorized } = useAccessLevel()
  const [searchParams] = useSearchParams()
  const [user, setUser] = useState(email)
  const [pii, setPii] = useState<any[]>([])
  const { data, loading, refetch } = useOktaUserQuery({
    variables: { username: user },
    skip: !user
  })

  useEffect(() => {
    const query = searchParams.get('email') ?? ''
    setUser(query)
    refetch()
    const profile = data ? data?.oktaUserProfile : undefined
    if (profile && pii.length === 0) {
      const piiObject = [
        { key: 'Username', value: profile.login ?? '' },
        { key: 'Email', value: profile.email ?? '' },
        { key: 'Street Address', value: profile.streetAddress ?? '' },
        { key: 'First Name', value: profile.firstName ?? '' },
        { key: 'Last Name', value: profile.lastName ?? '' },
        { key: 'Mobile Phone', value: profile.mobilePhone ?? '' },
        { key: 'Primary Phone Number', value: profile.primaryPhone ?? '' },
        { key: 'Company', value: profile.company ?? '' },
        { key: 'Country', value: profile.country ?? '' },
        { key: 'State', value: profile.state ?? '' },
        { key: 'City', value: profile.city ?? '' },
        { key: 'Geo Country', value: profile.geo_country ?? '' },
        { key: 'Geo Country Code', value: profile.geo_country_code ?? '' },
        { key: 'Geo Region', value: profile.geo_region ?? '' },
        { key: 'Zip Code', value: profile.zipCode ?? '' },
        { key: 'Job Level', value: profile.job_function ?? '' },
        { key: 'Job Function', value: profile.job_level ?? '' },
        { key: 'Title', value: profile.title ?? '' },
        { key: 'Secondary email', value: profile.secondEmail ?? '' },
        { key: 'Personal Email', value: profile.personal_email ?? '' },
        { key: 'ip_address', value: profile.ip_address ?? '' },
        { key: 'Latitude', value: profile.latitude ?? '' },
        { key: 'Longitude', value: profile.longitude ?? '' },
        { key: 'Country Code', value: profile.countryCode ?? '' }
      ]
      setPii(piiObject)
    }
  }, [data, pii, refetch, searchParams])

  const convertObjectToCSV = (obj: any) => {
    if (!obj || !obj.oktaUserProfile || typeof obj.oktaUserProfile !== 'object')
      return ''
    const profile = obj.oktaUserProfile
    const piiObject = {
      Username: profile.login,
      Email: profile.email,
      'Street Address': profile.streetAddress,
      'First Name': profile.firstName,
      'Last Name': profile.lastName,
      'Mobile Phone': profile.mobilePhone,
      'Primary Phone Number': profile.primaryPhone,
      Company: profile.company,
      Country: profile.country,
      State: profile.state,
      City: profile.city,
      'Geo Country': profile.geo_country,
      'Geo Country Code': profile.geo_country_code,
      'Geo Region': profile.geo_region,
      'Zip Code': profile.zipCode,
      'Job Level': profile.job_function,
      'Job Function': profile.job_level,
      Title: profile.title,
      'Secondary email': profile.secondEmail,
      'Personal Email': profile.personal_email,
      ip_address: profile.ip_address,
      Latitude: profile.latitude,
      Longitude: profile.longitude,
      'Country Code': profile.countryCode
    }
    const rows = Object.entries(piiObject).map(
      ([key, value]) => `${JSON.stringify(key)},${JSON.stringify(value)}`
    )
    return `Key,Value\n${rows.join('\n')}`
  }

  const downloadCSV = (csv: any, filename: string = 'data.csv') => {
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)
  }
  const handleDownload = () => {
    if (!data || loading) return
    const csv = convertObjectToCSV(data)
    downloadCSV(csv, 'pii_report.csv')
  }

  if (isAuthorized(AppFunction.PIIReport)) {
    return (
      <div className="card" style={{ paddingBottom: 30 }}>
        <button
          type="button"
          className="btn btn-primary"
          style={{ marginBottom: 10 }}
          onClick={handleDownload}
          disabled={loading || !data}
        >
          <span>Download PII Report</span>
        </button>
        <div>
          <DataTable value={pii} loading={loading} responsiveLayout="scroll">
            {/* eslint-disable-next-line */}
            <Column field="key" header="Key"></Column>
            {/* eslint-disable-next-line */}
            <Column field="value" header="Value"></Column>
          </DataTable>
        </div>
      </div>
    )
  }
  return <div>You do not have access</div>
}
